import type { ReactNode } from "react";
import clsx from "clsx";
import type { BattleBracketRosterEntry, BattleBracketView, GuildBattleCurrentMatch } from "../../api/guild";
import type { BattleLogEvent } from "../../types";

export interface LineWarBenchView {
  allyTitle: string;
  enemyTitle: string;
  ally: BattleBracketRosterEntry[];
  enemy: BattleBracketRosterEntry[];
}

function titlesForMatch(
  match: GuildBattleCurrentMatch,
  bracket: BattleBracketView | null | undefined,
  allyTeam: 0 | 1,
): { allyTitle: string; enemyTitle: string } {
  const allyTitle =
    allyTeam === 0
      ? (match.guild_a_name ?? bracket?.guild_a_name ?? "Allies")
      : (match.guild_b_name ?? bracket?.guild_b_name ?? "Allies");
  const enemyTitle =
    allyTeam === 0
      ? (match.guild_b_name ?? bracket?.guild_b_name ?? "Enemies")
      : (match.guild_a_name ?? bracket?.guild_a_name ?? "Enemies");
  return { allyTitle, enemyTitle };
}

/**
 * Live bench from progressive log:
 * - INIT field units start on arena
 * - roster entries not yet on field → reserve (even if end-state says fallen)
 * - JOIN removes from reserve
 * - deaths of anyone who entered the field → fallen
 */
export function resolveLineWarBenchesFromLog(
  match: GuildBattleCurrentMatch,
  allyTeam: 0 | 1,
  visibleLog: BattleLogEvent[],
  bracket?: BattleBracketView | null,
): LineWarBenchView | null {
  const init = visibleLog.find((ev) => ev.action === "INIT" && ev.units?.length);
  if (!init?.units?.length) return null;

  const labels: Record<string, string> = {};
  const teamOf: Record<string, 0 | 1> = {};
  const fieldAtStart = new Set<string>();
  const hps: Record<string, number> = {};

  for (const u of init.units) {
    const team = (u.team === 1 ? 1 : 0) as 0 | 1;
    teamOf[u.uid] = team;
    labels[u.uid] = u.display_name ?? u.name;
    fieldAtStart.add(u.uid);
    hps[u.uid] = u.hp;
  }

  const rosterA = match.roster_a ?? [];
  const rosterB = match.roster_b ?? [];
  for (const [roster, team] of [
    [rosterA, 0],
    [rosterB, 1],
  ] as const) {
    for (const entry of roster) {
      if (!entry.uid) continue;
      if (teamOf[entry.uid] == null) teamOf[entry.uid] = team;
      if (!labels[entry.uid]) labels[entry.uid] = entry.label;
    }
  }

  const onField = new Set(fieldAtStart);
  const hasJoined = new Set<string>();

  for (const ev of visibleLog) {
    if (ev.action === "JOIN" && ev.units) {
      for (const u of ev.units) {
        const team = (u.team === 1 ? 1 : 0) as 0 | 1;
        teamOf[u.uid] = team;
        labels[u.uid] = u.display_name ?? u.name;
        hps[u.uid] = u.hp;
        onField.add(u.uid);
        hasJoined.add(u.uid);
      }
    }
    if (ev.action === "ATTACK" && ev.target != null && ev.target_hp !== undefined) {
      hps[ev.target] = ev.target_hp;
    }
    if (ev.hits) {
      for (const hit of ev.hits) {
        if (hit.target != null && hit.hp !== undefined) hps[hit.target] = hit.hp;
      }
    }
    if (ev.snapshots) {
      for (const [uid, snap] of Object.entries(ev.snapshots)) {
        hps[uid] = snap.hp;
      }
    }
  }

  const sideEntries = (team: 0 | 1): BattleBracketRosterEntry[] => {
    const roster = team === 0 ? rosterA : rosterB;
    const uids = new Set<string>([
      ...Object.keys(teamOf).filter((uid) => teamOf[uid] === team),
      ...roster.map((e) => e.uid).filter((uid): uid is string => Boolean(uid)),
    ]);

    const out: BattleBracketRosterEntry[] = [];
    for (const uid of uids) {
      const label = labels[uid] ?? roster.find((e) => e.uid === uid)?.label ?? uid;
      const entered = onField.has(uid);
      const hp = hps[uid];
      const dead = entered && hp !== undefined && hp <= 0;

      if (dead) {
        out.push({ uid, label, status: "fallen" });
        continue;
      }
      // Still waiting on bench (not yet joined / not in opening field)
      if (!entered) {
        out.push({ uid, label, status: "reserve" });
      }
    }
    return out;
  };

  const { allyTitle, enemyTitle } = titlesForMatch(match, bracket, allyTeam);
  return {
    allyTitle,
    enemyTitle,
    ally: sideEntries(allyTeam),
    enemy: sideEntries(allyTeam === 0 ? 1 : 0),
  };
}

export function resolveLineWarBenches(
  bracket: BattleBracketView | null | undefined,
  match: GuildBattleCurrentMatch,
  allyTeam: 0 | 1,
  visibleLog?: BattleLogEvent[],
): LineWarBenchView | null {
  if (visibleLog?.length) {
    const fromLog = resolveLineWarBenchesFromLog(match, allyTeam, visibleLog, bracket);
    if (fromLog) return fromLog;
  }

  // Prefer match rosters; empty array from match must not fall back to a stale bracket.
  const hasMatchRosters = match.roster_a != null || match.roster_b != null;
  const rosterA = hasMatchRosters ? (match.roster_a ?? []) : (bracket?.roster_a ?? []);
  const rosterB = hasMatchRosters ? (match.roster_b ?? []) : (bracket?.roster_b ?? []);
  if (!rosterA.length && !rosterB.length) return null;

  const allyRoster = allyTeam === 0 ? rosterA : rosterB;
  const enemyRoster = allyTeam === 0 ? rosterB : rosterA;

  // Without a live log, show end-state fallen as opening reserve so both sides appear.
  const toOpeningBench = (entries: BattleBracketRosterEntry[]) =>
    entries
      .filter((e) => e.status === "reserve" || e.status === "fallen")
      .map((e) => ({ ...e, status: "reserve" as const }));

  const { allyTitle, enemyTitle } = titlesForMatch(match, bracket, allyTeam);
  return {
    allyTitle,
    enemyTitle,
    ally: toOpeningBench(allyRoster),
    enemy: toOpeningBench(enemyRoster),
  };
}

interface GuildBattleReserveBenchProps {
  side: "left" | "right";
  title: string;
  entries: BattleBracketRosterEntry[];
}

function BenchColumn({ side, title, entries }: GuildBattleReserveBenchProps) {
  const reserves = entries.filter((e) => e.status === "reserve");
  const fallen = entries.filter((e) => e.status === "fallen");

  return (
    <aside
      className={clsx(
        "shrink-0 w-[4.5rem] sm:w-20 flex flex-col gap-1 py-2 px-1 border-kingdom-mid bg-kingdom-dark/80",
        side === "left" ? "border-r" : "border-l",
      )}
    >
      <p className="text-[8px] font-bold text-gray-500 uppercase text-center leading-tight line-clamp-2">{title}</p>
      <div className="space-y-1">
        <p className="text-[8px] text-violet-400 font-semibold text-center">Bench</p>
        {reserves.length === 0 ? (
          <p className="text-[8px] text-gray-600 text-center">—</p>
        ) : (
          reserves.map((e, i) => (
            <div
              key={`${e.uid ?? e.label}-reserve-${i}`}
              className="rounded-md border border-violet-900/60 bg-violet-950/30 px-1 py-1 text-center"
            >
              <span className="text-[9px] text-violet-200 leading-tight line-clamp-2 block">{e.label}</span>
              <span className="text-[7px] text-violet-500">#{i + 1}</span>
            </div>
          ))
        )}
      </div>
      {fallen.length > 0 && (
        <div className="space-y-0.5 mt-auto">
          <p className="text-[8px] text-rose-500/80 font-semibold text-center">Fallen</p>
          {fallen.slice(-4).map((e, i) => (
            <p
              key={`${e.uid ?? e.label}-fallen-${i}`}
              className="text-[8px] text-gray-600 line-through text-center truncate px-0.5"
            >
              {e.label}
            </p>
          ))}
        </div>
      )}
    </aside>
  );
}

interface GuildBattleArenaWithBenchProps {
  bench: LineWarBenchView;
  children: ReactNode;
}

export function GuildBattleArenaWithBench({ bench, children }: GuildBattleArenaWithBenchProps) {
  return (
    <div className="flex-1 relative min-h-0 flex flex-row">
      <BenchColumn side="left" title={bench.allyTitle} entries={bench.ally} />
      <div className="flex-1 min-w-0 relative">{children}</div>
      <BenchColumn side="right" title={bench.enemyTitle} entries={bench.enemy} />
    </div>
  );
}
